// BlockWarning.js - Enhanced 3-State System (PENDING / BLOCKED / PERMANENTLY_BLOCKED)
import React, { useState, useEffect } from 'react';
import BodyScrollLock from "./components/BodyScrollLock";

function BlockWarning({ user, token, onLogout, onContinue }) {
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [ownerTag, setOwnerTag] = useState('mert_xyz');

  useEffect(() => {
    loadUserStatus();
  }, []);

  const loadUserStatus = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUserStatus(data);
        setOwnerTag(data.owner_discord_tag || 'mert_xyz');
      }
    } catch (error) {
      console.error('User status error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBlockWarning = async () => {
    setConfirming(true);

    try {
      const response = await fetch('/api/admin/confirm-warning', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUserStatus(prev => ({
          ...prev,
          blocked_at: data.blocked_at,
          deletion_date: data.deletion_date
        }));
      }
    } catch (error) {
      console.error('Confirm error:', error);
      alert('Fehler beim Bestätigen');
    } finally {
      setConfirming(false);
    }
  };

  const getTimeRemaining = () => {
    if (!userStatus?.deletion_date) return null;
    
    const now = new Date();
    const deletion = new Date(userStatus.deletion_date);
    const diff = deletion - now;
    
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50">
        <BodyScrollLock />
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Lade Status...</p>
        </div>
      </div>
    );
  }

  if (!userStatus) return null;

  const timeRemaining = getTimeRemaining();

  // ============================================================================
  // MODE 1: PENDING - Warte auf Freischaltung
  // ============================================================================
  if (userStatus.status === 'pending') {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
        <div className="relative max-w-2xl w-full">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded-3xl blur-2xl animate-pulse"></div>
          
          <div className="relative bg-gradient-to-br from-slate-900 via-yellow-900/20 to-slate-900 border-2 border-yellow-500/50 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-600 to-orange-600 px-6 sm:px-8 py-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">⚠️ Account nicht freigeschaltet</h1>
              <p className="text-yellow-100 text-sm sm:text-base">Warte auf Administrator-Freigabe</p>
            </div>

            <div className="px-6 sm:px-8 py-8 space-y-6">
              <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-2xl p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Bitte warten
                </h2>
                <div className="space-y-3 text-slate-300 text-sm sm:text-base">
                  <p>Dein Account wurde erfolgreich erstellt, muss aber noch vom Administrator <strong className="text-white">freigeschaltet</strong> werden.</p>
                  <p className="text-yellow-400 font-semibold">
                    Du wirst benachrichtigt, sobald du Zugriff erhältst.
                  </p>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4">
                <p className="text-blue-300 text-xs sm:text-sm flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Bei Fragen kontaktiere bitte <strong className="text-white">@{ownerTag}</strong> auf Discord.</span>
                </p>
              </div>

              <button
                onClick={onLogout}
                className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all text-sm sm:text-base"
              >
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MODE 2: BLOCKED - 3 Tage Schonfrist
  // ============================================================================
  if (userStatus.status === 'blocked') {
    const hasSeenWarning = !!userStatus.blocked_at;

    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
        <div className="relative max-w-2xl w-full">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/30 via-orange-500/30 to-red-500/30 rounded-3xl blur-2xl animate-pulse"></div>
          
          <div className="relative bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 border-2 border-red-500/50 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 sm:px-8 py-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">⚠️ ACCOUNT GESPERRT ⚠️</h1>
              <p className="text-red-100 text-sm sm:text-base">Dein Zugriff wurde eingeschränkt</p>
            </div>

            <div className="px-6 sm:px-8 py-8 space-y-6">
              {!hasSeenWarning ? (
                /* INITIAL WARNING - Noch nicht bestätigt */
                <>
                  <div className="bg-red-500/10 border-2 border-red-500/30 rounded-2xl p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Wichtige Information
                    </h2>
                    <div className="space-y-3 text-slate-300 text-sm sm:text-lg">
                      <p>Dein Account wurde vom Administrator <strong className="text-white">gesperrt</strong>.</p>
                      <p className="text-red-400 font-bold text-base sm:text-xl">
                        ⏰ Du hast <span className="text-2xl sm:text-3xl">3 TAGE</span> Zeit, um wichtige Daten zu sichern.
                      </p>
                      <p className="text-sm sm:text-base">Nach Ablauf dieser Frist werden <strong className="text-red-400">ALLE deine Daten unwiderruflich gelöscht</strong>:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4 text-xs sm:text-base">
                        <li>Alle hochgeladenen Dateien</li>
                        <li>Share-Links und Downloads</li>
                        <li>Dein Account und alle Metadaten</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-orange-500/10 border border-orange-400/30 rounded-xl p-4">
                    <p className="text-orange-300 text-xs sm:text-sm flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Der 3-Tage-Countdown beginnt, sobald du auf "Verstanden" klickst. Du kannst weiterhin Dateien herunterladen, aber <strong>keine neuen hochladen</strong>.</span>
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleConfirmBlockWarning}
                      disabled={confirming}
                      className="flex-1 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white text-base sm:text-lg font-bold rounded-xl hover:shadow-lg hover:shadow-red-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {confirming ? 'Wird bestätigt...' : 'Verstanden - Countdown starten'}
                    </button>
                    <button
                      onClick={onLogout}
                      className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all text-sm sm:text-base"
                    >
                      Abmelden
                    </button>
                  </div>
                </>
              ) : (
                /* COUNTDOWN ACTIVE - Warnung wurde bestätigt */
                <>
                  <div className="text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 bg-red-500/20 rounded-full border-4 border-red-500/50">
                      <div className="text-center">
                        <div className="text-4xl sm:text-5xl font-bold text-red-400">{timeRemaining?.days || 0}</div>
                        <div className="text-xs sm:text-sm text-slate-400">Tage</div>
                      </div>
                    </div>

                    {timeRemaining && (
                      <div className="text-slate-300 text-sm sm:text-base">
                        <span className="text-red-400 font-bold text-lg sm:text-xl">{timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m</span>
                        <p className="text-xs sm:text-sm mt-1">verbleibend</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <h2 className="text-xl sm:text-2xl font-bold text-white">Löschung geplant</h2>
                      <p className="text-red-400 font-semibold text-sm sm:text-lg">
                        {formatDate(userStatus.deletion_date)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-red-500/10 border-2 border-red-500/30 rounded-2xl p-6 text-center">
                    <p className="text-slate-300 text-sm sm:text-lg mb-4">
                      Deine Daten werden in <strong className="text-red-400">{timeRemaining?.days || 0} Tagen</strong> automatisch gelöscht.
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm">
                      Warnung bestätigt am: {formatDate(userStatus.blocked_at)}
                    </p>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-xl p-4">
                    <p className="text-yellow-300 text-xs sm:text-sm flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Du kannst dich weiterhin anmelden um deine Dateien <strong>herunterzuladen</strong>, aber keine neuen hochladen.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={onContinue}
                      className="flex-1 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all text-sm sm:text-base"
                    >
                      Dateien herunterladen
                    </button>
                    <button
                      onClick={onLogout}
                      className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all text-sm sm:text-base"
                    >
                      Abmelden
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MODE 3: PERMANENTLY_BLOCKED - Dauerhaft gesperrt
  // ============================================================================
  if (userStatus.status === 'permanently_blocked') {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-50 p-4">
        <BodyScrollLock />
        <div className="relative max-w-2xl w-full">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/40 to-black/40 rounded-3xl blur-2xl"></div>
          
          <div className="relative bg-gradient-to-br from-slate-950 via-red-950/30 to-slate-950 border-2 border-red-600/70 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-700 to-red-900 px-6 sm:px-8 py-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/30 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-400/50">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">🚫 Zugriff dauerhaft gesperrt</h1>
              <p className="text-red-200 text-sm sm:text-base">Dein Account wurde permanent deaktiviert</p>
            </div>

            <div className="px-6 sm:px-8 py-8 space-y-6">
              <div className="bg-red-600/10 border-2 border-red-600/40 rounded-2xl p-6 text-center">
                <svg className="w-16 h-16 sm:w-20 sm:h-20 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Account permanent gesperrt</h2>
                <div className="space-y-3 text-slate-300 text-sm sm:text-base">
                  <p>Die 3-Tage-Schonfrist ist abgelaufen.</p>
                  <p className="text-red-400 font-bold">Alle deine Daten wurden <span className="text-red-300">unwiderruflich gelöscht</span>.</p>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4">
                <p className="text-blue-300 text-xs sm:text-sm flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <span>Bei Fragen zur Entsperrung kontaktiere bitte <strong className="text-white">@{ownerTag}</strong> auf Discord.</span>
                </p>
              </div>

              <button
                onClick={onLogout}
                className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all text-sm sm:text-base"
              >
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Kein Warning nötig - approved user
  return null;
}

export default BlockWarning;
